import { useEffect, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Typography, message } from 'antd';
import {
  TeamOutlined,
  UserOutlined,
  CrownOutlined,
  RobotOutlined,
  StarOutlined,
  RocketOutlined,
  PauseCircleOutlined,
  UserAddOutlined,
  ShareAltOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { businessApi, providerApi } from '../../api/businessApi';
import { useBusinessStore } from '../../store/businessStore';
import { telegram } from '../../utils/telegram';

const { Text } = Typography;

// ─── Plan visual configuration ──────────────────────────────────────────────

const PLAN_META: Record<string, { icon: ReactNode; gradient: string }> = {
  FREE:    { icon: <StarOutlined />,    gradient: 'linear-gradient(135deg, #1a3a5c 0%, #3390ec 100%)' },
  PRO:     { icon: <CrownOutlined />,   gradient: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 60%, #60a5fa 100%)' },
  PREMIUM: { icon: <RocketOutlined />,  gradient: 'linear-gradient(135deg, #7c2d12 0%, #d97706 60%, #fbbf24 100%)' },
};

interface NavCounts {
  providers: number;
  customers: number;
}

// ─── Hero card ──────────────────────────────────────────────────────────────

function HeroCard() {
  const { t } = useTranslation('owner');
  const { selectedBusiness } = useBusinessStore();
  if (!selectedBusiness) return null;

  const plan = PLAN_META[selectedBusiness.subscriptionPlan] ?? PLAN_META.FREE;
  const planLabel = t(`overview.plans.${selectedBusiness.subscriptionPlan}`, {
    defaultValue: selectedBusiness.subscriptionPlan,
  });
  const initial = selectedBusiness.businessName.charAt(0).toUpperCase();

  return (
    <div
      style={{
        position: 'relative',
        borderRadius: 20,
        overflow: 'hidden',
        background: plan.gradient,
        boxShadow: '0 8px 28px rgba(15,23,42,0.18)',
      }}
    >
      {/* Decorative orbs */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: -70,
          right: -50,
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.10)',
          filter: 'blur(8px)',
          pointerEvents: 'none',
        }}
      />
      <div
        aria-hidden
        style={{
          position: 'absolute',
          bottom: -60,
          left: -40,
          width: 160,
          height: 160,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.06)',
          filter: 'blur(8px)',
          pointerEvents: 'none',
        }}
      />
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(at 20% 25%, rgba(255,255,255,0.12) 0%, transparent 50%)',
          pointerEvents: 'none',
        }}
      />

      {/* Content row */}
      <div style={{ position: 'relative', padding: '20px 16px 16px', display: 'flex', alignItems: 'center', gap: 14 }}>
        {/* Logo / initials */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <div
            aria-hidden
            style={{
              position: 'absolute',
              inset: -5,
              borderRadius: 18,
              background: 'rgba(255,255,255,0.18)',
              filter: 'blur(4px)',
            }}
          />
          <div
            style={{
              position: 'relative',
              width: 64,
              height: 64,
              borderRadius: 16,
              background: selectedBusiness.logoUrl ? 'transparent' : 'rgba(255,255,255,0.22)',
              border: '2px solid rgba(255,255,255,0.42)',
              boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 26,
              fontWeight: 800,
              color: '#fff',
              overflow: 'hidden',
              letterSpacing: -0.4,
            }}
          >
            {selectedBusiness.logoUrl ? (
              <img
                src={selectedBusiness.logoUrl}
                alt="logo"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              initial
            )}
          </div>
        </div>

        {/* Name + bot */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text
            style={{
              display: 'block',
              fontSize: 19,
              fontWeight: 800,
              color: '#fff',
              letterSpacing: -0.3,
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {selectedBusiness.businessName}
          </Text>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 4 }}>
            <RobotOutlined style={{ fontSize: 12, color: 'rgba(255,255,255,0.78)' }} />
            <Text
              style={{
                fontSize: 13,
                color: 'rgba(255,255,255,0.78)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              @{selectedBusiness.botUsername}
            </Text>
          </div>
        </div>
      </div>

      {/* Plan + status row */}
      <div
        style={{
          position: 'relative',
          background: 'rgba(0,0,0,0.18)',
          padding: '10px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            background: 'rgba(255,255,255,0.18)',
            border: '1px solid rgba(255,255,255,0.20)',
            borderRadius: 999,
            padding: '4px 11px',
          }}
        >
          <span style={{ fontSize: 13, color: '#fff' }}>{plan.icon}</span>
          <Text style={{ fontSize: 13, fontWeight: 700, color: '#fff', letterSpacing: 0.1 }}>
            {planLabel}
          </Text>
        </div>

        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            background: selectedBusiness.active
              ? 'rgba(34,197,94,0.22)'
              : 'rgba(255,255,255,0.12)',
            border: selectedBusiness.active
              ? '1px solid rgba(74,222,128,0.30)'
              : '1px solid rgba(255,255,255,0.16)',
            borderRadius: 999,
            padding: '4px 11px',
          }}
        >
          {selectedBusiness.active ? (
            <span
              aria-hidden
              style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: '#4ade80',
                boxShadow: '0 0 0 3px rgba(74,222,128,0.28)',
              }}
            />
          ) : (
            <PauseCircleOutlined style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }} />
          )}
          <Text
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: selectedBusiness.active ? '#4ade80' : 'rgba(255,255,255,0.7)',
              letterSpacing: 0.1,
            }}
          >
            {selectedBusiness.active ? t('overview.status.active') : t('overview.status.inactive')}
          </Text>
        </div>
      </div>
    </div>
  );
}

// ─── Section caption ────────────────────────────────────────────────────────

function SectionCaption({ children }: { children: ReactNode }) {
  return (
    <Text
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: 'var(--bl-text-muted, #9ca3af)',
        textTransform: 'uppercase',
        letterSpacing: 0.6,
        display: 'block',
        marginTop: 6,
        marginBottom: 8,
        paddingLeft: 4,
      }}
    >
      {children}
    </Text>
  );
}

// ─── Action tile (quick action card) ────────────────────────────────────────

interface ActionTileProps {
  icon: ReactNode;
  color: string;
  title: string;
  subtitle: string;
  onClick: () => void;
}

function ActionTile({ icon, color, title, subtitle, onClick }: ActionTileProps) {
  const [pressed, setPressed] = useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      onTouchStart={() => setPressed(true)}
      onTouchEnd={() => setPressed(false)}
      onTouchCancel={() => setPressed(false)}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      style={{
        flex: 1,
        minWidth: 0,
        background: 'var(--bl-surface, #fff)',
        border: '1px solid var(--bl-divider, #ebebeb)',
        borderRadius: 14,
        padding: '12px 12px 12px 12px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 8,
        cursor: 'pointer',
        transform: pressed ? 'scale(0.98)' : 'scale(1)',
        transition: 'transform 0.1s ease, box-shadow 0.15s ease',
        boxShadow: pressed
          ? '0 1px 4px rgba(0,0,0,0.04)'
          : 'var(--bl-shadow-soft, 0 2px 10px rgba(0,0,0,0.04))',
        WebkitTapHighlightColor: 'transparent',
        outline: 'none',
        textAlign: 'left',
        fontFamily: 'inherit',
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 11,
          background: `linear-gradient(135deg, ${color}14 0%, ${color}28 100%)`,
          border: `1px solid ${color}1f`,
          color,
          fontSize: 16,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div style={{ minWidth: 0, width: '100%' }}>
        <Text
          style={{
            display: 'block',
            fontSize: 13,
            fontWeight: 700,
            color: 'var(--bl-text-strong, #0f172a)',
            letterSpacing: -0.1,
            lineHeight: 1.25,
            whiteSpace: 'normal',
          }}
        >
          {title}
        </Text>
        <Text
          style={{
            display: 'block',
            fontSize: 11,
            color: 'var(--bl-text-muted, #6b7280)',
            marginTop: 2,
            lineHeight: 1.35,
          }}
        >
          {subtitle}
        </Text>
      </div>
    </button>
  );
}

// ─── Navigation card (manage list) ──────────────────────────────────────────

interface NavCardProps {
  icon: ReactNode;
  color: string;
  title: string;
  subtitle: string;
  count?: number;
  onClick: () => void;
}

function NavCard({ icon, color, title, subtitle, count, onClick }: NavCardProps) {
  const [pressed, setPressed] = useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      onTouchStart={() => setPressed(true)}
      onTouchEnd={() => setPressed(false)}
      onTouchCancel={() => setPressed(false)}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      style={{
        width: '100%',
        background: 'var(--bl-surface, #fff)',
        border: '1px solid var(--bl-divider, #ebebeb)',
        borderRadius: 14,
        padding: '12px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        cursor: 'pointer',
        transform: pressed ? 'scale(0.99)' : 'scale(1)',
        transition: 'transform 0.1s ease, box-shadow 0.15s ease',
        boxShadow: pressed
          ? '0 1px 4px rgba(0,0,0,0.04)'
          : 'var(--bl-shadow-soft, 0 2px 10px rgba(0,0,0,0.04))',
        WebkitTapHighlightColor: 'transparent',
        outline: 'none',
        textAlign: 'left',
        fontFamily: 'inherit',
      }}
    >
      <div
        style={{
          width: 42,
          height: 42,
          borderRadius: 12,
          background: `linear-gradient(135deg, ${color}14 0%, ${color}28 100%)`,
          border: `1px solid ${color}1f`,
          color,
          fontSize: 18,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Text
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: 'var(--bl-text-strong, #0f172a)',
              letterSpacing: -0.1,
            }}
          >
            {title}
          </Text>
          {count !== undefined && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: 'var(--bl-text-muted, #6b7280)',
                background: 'var(--bl-surface-soft, #f0f4f8)',
                border: '1px solid var(--bl-divider, #e5e7eb)',
                borderRadius: 999,
                padding: '1px 7px',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {count}
            </span>
          )}
        </div>
        <Text
          style={{
            fontSize: 12,
            color: 'var(--bl-text-muted, #6b7280)',
            display: 'block',
            marginTop: 1,
            lineHeight: 1.35,
          }}
        >
          {subtitle}
        </Text>
      </div>
      <RightOutlined
        style={{
          fontSize: 11,
          color: 'var(--bl-text-muted, #c0c5cf)',
          flexShrink: 0,
        }}
      />
    </button>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────

interface Props {
  onNavigateToCustomers?: () => void;
  onNavigateToProviders?: () => void;
}

export default function OverviewSection({
  onNavigateToCustomers,
  onNavigateToProviders,
}: Props) {
  const { t } = useTranslation('owner');
  const { selectedBusiness } = useBusinessStore();
  const [counts, setCounts] = useState<NavCounts | null>(null);

  useEffect(() => {
    if (!selectedBusiness) return;
    let cancelled = false;
    (async () => {
      try {
        const [providers, customersCount] = await Promise.all([
          providerApi.getAll(selectedBusiness.id),
          businessApi.getCustomersCount(selectedBusiness.id),
        ]);
        if (cancelled) return;
        setCounts({
          providers: providers.length,
          customers: customersCount,
        });
      } catch {
        if (!cancelled) void message.error(t('overview.stats.loadError'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedBusiness, t]);

  if (!selectedBusiness) return null;

  const handleInvite = () => {
    telegram.hapticFeedback.impact('light');
    onNavigateToProviders?.();
  };

  const handleShareBot = async () => {
    const link = `https://t.me/${selectedBusiness.botUsername}`;
    try {
      await navigator.clipboard.writeText(link);
      telegram.hapticFeedback.notification('success');
      void message.success(t('overview.quickActions.shareBot.copied'));
    } catch {
      telegram.hapticFeedback.notification('error');
      void message.error(t('overview.quickActions.shareBot.copyFailed'));
    }
  };

  const sectionGap: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 10 };

  return (
    <div style={sectionGap}>
      {/* Hero */}
      <div className="bl-animate-in">
        <HeroCard />
      </div>

      {/* Quick actions */}
      <div className="bl-animate-in bl-animate-in-delay-1">
        <SectionCaption>{t('overview.quickActions.caption')}</SectionCaption>
        <div style={{ display: 'flex', gap: 8 }}>
          <ActionTile
            icon={<UserAddOutlined />}
            color="#3390ec"
            title={t('overview.quickActions.invite.title')}
            subtitle={t('overview.quickActions.invite.subtitle')}
            onClick={handleInvite}
          />
          <ActionTile
            icon={<ShareAltOutlined />}
            color="#9254de"
            title={t('overview.quickActions.shareBot.title')}
            subtitle={t('overview.quickActions.shareBot.subtitle')}
            onClick={() => void handleShareBot()}
          />
        </div>
      </div>

      {/* Manage */}
      <div className="bl-animate-in bl-animate-in-delay-2">
        <SectionCaption>{t('overview.manage.caption')}</SectionCaption>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <NavCard
            icon={<TeamOutlined />}
            color="#3390ec"
            title={t('overview.manage.providers.title')}
            subtitle={t('overview.manage.providers.subtitle')}
            count={counts?.providers}
            onClick={() => onNavigateToProviders?.()}
          />
          <NavCard
            icon={<UserOutlined />}
            color="#1f8b4c"
            title={t('overview.manage.customers.title')}
            subtitle={t('overview.manage.customers.subtitle')}
            count={counts?.customers}
            onClick={() => onNavigateToCustomers?.()}
          />
        </div>
      </div>
    </div>
  );
}

